import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Home,
  Heart,
  MapPin,
  Star,
  ArrowRight,
  Users
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const MelbourneBeachPage = () => {
  const services = [
    {
      title: "Recurring House Cleaning",
      description: "Regular cleaning for Melbourne Beach residents. Weekly or bi-weekly service with the same trusted team every visit."
    },
    {
      title: "Deep Cleaning",
      description: "Comprehensive beachside cleaning for homes dealing with sand, salt air, and Florida's humidity."
    },
    {
      title: "Move-In / Move-Out Cleaning",
      description: "Inspection-ready cleaning for Melbourne Beach home transitions and rental turnovers."
    },
    {
      title: "Vacation Rental Cleaning",
      description: "Professional turnover service for beachside vacation rentals with guest-ready standards."
    }
  ];

  const perfectFor = [
    "Year-round beachside residents",
    "Families seeking a quieter beachside community",
    "Retirees enjoying coastal living",
    "Homeowners near Floridana Beach",
    "Properties along Ocean Avenue",
    "Vacation rental owners in quiet beachside areas"
  ];

  const faqs = [
    {
      question: "Do you serve all of Melbourne Beach?",
      answer: "Yes. We serve all Melbourne Beach neighborhoods—from Floridana Beach to the residential areas along Ocean Avenue and throughout the community. Melbourne Beach may be smaller than some neighboring towns, but we know it well."
    },
    {
      question: "How is cleaning a Melbourne Beach home different from other areas?",
      answer: "Melbourne Beach homes face the same beachside challenges as other coastal communities—sand, salt air, humidity—but the quieter pace means many of our clients here are year-round residents rather than high-turnover vacation rentals. We tailor our service to your needs."
    },
    {
      question: "What makes Pierce Premier different from other cleaning companies?",
      answer: "We're locally owned by Bob and Heather, not a franchise. You'll have the same cleaners every visit, and Heather's 15+ years of experience means we understand how to care for beachside homes properly."
    },
    {
      question: "Can you work around our schedule?",
      answer: "Absolutely. We're flexible and work with your availability. Many Melbourne Beach residents appreciate that we're easy to reach and accommodating."
    },
    {
      question: "Do you clean vacation rental properties in Melbourne Beach?",
      answer: "Yes. While Melbourne Beach is quieter than busier tourist areas, we do handle vacation rental turnovers for properties throughout the community."
    }
  ];

  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "House Cleaning Services in Melbourne Beach, FL | Pierce Premier",
      description: "Residential house cleaning in Melbourne Beach, FL. Quiet beachside specialists. Same team every visit, 15+ years experience, fully insured. Call 321-408-1347.",
      url: "/areas/melbourne-beach"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Service Areas", url: "/areas" },
      { name: "Melbourne Beach" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "House Cleaning Services in Melbourne Beach",
      description: "Professional residential cleaning services in Melbourne Beach, FL including recurring cleaning, deep cleaning, move-in/move-out, and vacation rental turnover."
    }),
    generateFAQSchema(faqs)
  );

  return (
    <>
      <SEO 
        title="House Cleaning Services in Melbourne Beach, FL | Pierce Premier"
        description="Residential house cleaning in Melbourne Beach, FL. Quiet beachside specialists. Same team every visit, 15+ years experience, fully insured. Call 321-408-1347."
        canonical="/areas/melbourne-beach"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        <section className="bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-sky-600" />
                  <Badge className="bg-sky-600 text-white">Quiet Beachside</Badge>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  House Cleaning Services in Melbourne Beach, FL
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  Melbourne Beach offers a quieter alternative to busier beach towns—residential neighborhoods, family-friendly beaches, and that small-community feel. Pierce Premier serves year-round residents and beachside homeowners who appreciate the peaceful pace this area offers.
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  Whether you live near Floridana Beach, along Ocean Avenue, or anywhere in this close-knit community, we bring professional cleaning services with a personal touch.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-sky-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-sky-700 transition-colors"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-sky-600 text-sky-600 px-8 py-4 rounded-lg font-semibold hover:bg-sky-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?auto=format&fit=crop&w=800&q=80" 
                  alt="Residential house cleaning in Melbourne Beach, Florida"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Melbourne Beach Residents Choose Pierce Premier
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-sky-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Heart className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">We Value Your Community</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Melbourne Beach residents chose this area for its quieter pace and residential feel. We respect that and bring the same professionalism and care you'd expect from any service provider in this community.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-sky-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Same Team Every Time</h3>
                  <p className="text-gray-600 leading-relaxed">
                    No rotating crews. You'll have the same cleaners every visit, so they learn your home and your preferences. That consistency matters when you're building a long-term relationship.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-sky-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Beachside Expertise</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Living near the ocean means dealing with sand, salt air, and humidity year-round. Heather's 15+ years of experience includes plenty of beachside homes, so we know how to protect your investment.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-sky-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Locally Owned & Accountable</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Bob and Heather personally oversee every job. We're not a franchise—we're a local business that stands behind our work and values the relationships we build.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-2">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  Why We Love Serving Melbourne Beach
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Melbourne Beach feels different from the busier beach towns—it's residential, quieter, and there's a real sense of community. People who live here chose it for those reasons, and we appreciate the opportunity to serve families and homeowners who value that peaceful beachside lifestyle.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  It's the kind of place where you can actually enjoy living near the ocean without the constant tourist traffic, and we're glad to help keep your home clean so you can enjoy it.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Services for Melbourne Beach Homes
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, idx) => (
                <Card key={idx} className="border-2 hover:border-sky-200 transition-colors">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Perfect For Melbourne Beach
              </h2>
              <p className="text-gray-600">Pierce Premier is trusted by:</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {perfectFor.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-sky-100">
                  <CheckCircle2 className="w-5 h-5 text-sky-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Melbourne Beach Areas We Serve
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                "Floridana Beach",
                "Ocean Avenue",
                "A1A Beachside",
                "Residential Neighborhoods",
                "Near Spessard Holland Park"
              ].map((area, idx) => (
                <div key={idx} className="flex items-center gap-2 p-3 bg-sky-50 rounded-lg border border-sky-100">
                  <CheckCircle2 className="w-5 h-5 text-sky-600 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{area}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-2">
                <span className="font-semibold">ZIP Code:</span> 32951
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                We Also Serve Nearby Communities
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { name: "Indialantic", link: "/areas/indialantic" },
                { name: "Indian Harbour Beach", link: "/areas/indian-harbour-beach" },
                { name: "Satellite Beach", link: "/areas/satellite-beach" },
                { name: "Melbourne", link: "/areas/melbourne" },
                { name: "Palm Bay", link: "/areas/palm-bay" },
                { name: "West Melbourne", link: "#" }
              ].map((city, idx) => (
                city.link === "#" ? (
                  <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center">
                    <span className="text-gray-600">{city.name}</span>
                  </div>
                ) : (
                  <Link key={idx} to={city.link} className="p-4 bg-sky-50 rounded-lg border-2 border-sky-200 hover:border-sky-400 transition-colors text-center group">
                    <span className="text-sky-700 font-semibold group-hover:text-sky-900">{city.name}</span>
                    <ArrowRight className="w-4 h-4 inline-block ml-1 text-sky-600" />
                  </Link>
                )
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common Questions from Melbourne Beach Residents
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <CTASection 
          title="Ready for Reliable Beachside Cleaning?"
          description="Let Pierce Premier take care of your Melbourne Beach home. Same trusted team, beachside expertise, and the quality you deserve."
          primaryButtonText="Get Your Free Quote"
        />
      </div>
    </>
  );
};

export default MelbourneBeachPage;